import { Component, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import {
  customerIsMslRestricted,
  customerIsOgRestricted,
  customerIsUnRestricted,
} from '@shared/helpers/customer.helpers';
import { PastOrdersRow } from '@shared/models/my-orders-view.model';
import { Product } from '@shared/models/product.model';
import { OrderItem } from '@usf/ngrx-order';
import { ProductPropertiesEnum } from '@usf/product-types';
import { Observable } from 'rxjs';
import { OrderTracingService } from '../../order/tracking/tracing/order-tracing.service';
import { OrderService } from '@order/services/order.service';
import { mslProductHasSecondaries } from '../../shared/selectors/helpers/product-info.selectors.helper';

@Component({
  template: '',
})
export class CopyOrderModalComponent {
  @Input() pastOrderRow: PastOrdersRow;
  @Input() productMap$: Observable<Map<number, Product>>;
  @Input() ordersLoading: boolean;

  orderContainsAvailableItems = false;

  constructor(
    readonly platformService: PlatformService,
    private modalController: ModalController,
    readonly translateService: TranslateService,
    private orderTracingService: OrderTracingService,
    private orderService: OrderService,
  ) {}

  getUnavailableItemsText(
    orderItems: OrderItem[],
    productMap: Map<number, Product>,
  ): string {
    let csCount = 0;
    let eaCount = 0;
    orderItems.forEach(item => {
      if (this.isProductUnavailable(productMap.get(item.productNumber))) {
        csCount += item?.unitsOrdered?.currentValue || 0;
        eaCount += item?.eachesOrdered?.currentValue || 0;
      }
    });
    let unavailableText = '';
    if (csCount + eaCount > 0) {
      unavailableText =
        this.translateService.instant('i18n.orderStatusPage.notAvailable') +
        ': ';
    }
    if (csCount > 0) {
      unavailableText =
        unavailableText +
        csCount +
        ' ' +
        this.translateService.instant('i18n.common.cs');
    }
    if (csCount > 0 && eaCount > 0) {
      unavailableText = unavailableText + ' / ';
    }
    if (eaCount > 0) {
      unavailableText =
        unavailableText +
        eaCount +
        ' ' +
        this.translateService.instant('i18n.common.ea');
    }
    return unavailableText;
  }

  isProductUnavailable(product: Product): boolean {
    if (
      product?.inventory?.cashCarryIndicator === '2' ||
      product?.inventory?.cashCarryIndicator === '3' ||
      product?.summary?.properties.has(ProductPropertiesEnum.specialOrder) ||
      product?.summary?.properties.has(ProductPropertiesEnum.discontinued)
    ) {
      return true;
    }
    //og restricted products
    if (
      !(
        customerIsUnRestricted(this.pastOrderRow?.customer) ||
        (customerIsMslRestricted(this.pastOrderRow?.customer) &&
          mslProductHasSecondaries(product?.mslProduct)) ||
        (customerIsOgRestricted(this.pastOrderRow?.customer) &&
          product?.summary?.properties?.has(ProductPropertiesEnum.onOrderGuide))
      )
    ) {
      return true;
    }
    //msl restricted products
    if (
      customerIsMslRestricted(this.pastOrderRow?.customer) &&
      !product?.mslProduct?.isOrderableOnMsl &&
      !mslProductHasSecondaries(product?.mslProduct) &&
      !product?.orderable
    ) {
      return true;
    }
    return false;
  }

  allProductsLoaded(
    productMap: Map<number, Product>,
    orderItems: OrderItem[],
  ): boolean {
    return orderItems.every(orderItem => {
      return (
        !!productMap.get(orderItem?.productNumber)?.summary &&
        !!productMap.get(orderItem?.productNumber)?.productNumber
      );
    });
  }

  isOrderContainingAvailableItems(
    orderItems: OrderItem[],
    productMap: Map<number, Product>,
  ): boolean {
    const availableItems = orderItems.filter(item => {
      return !this.isProductUnavailable(productMap.get(item?.productNumber));
    });
    this.orderContainsAvailableItems = !!availableItems.length;
    return !!availableItems.length;
  }

  async copyOrder(productMap: Map<number, Product>) {
    this.orderTracingService.traceCopyOrderClickedOnModal(
      this.pastOrderRow?.orderHeader,
    );
    let availableItems: OrderItem[] = [];
    for (let orderItem of this.pastOrderRow?.orderItems) {
      if (!this.isProductUnavailable(productMap.get(orderItem.productNumber)))
        availableItems.push(orderItem);
    }
    this.orderService.copyOrder(
      {
        orderHeader: this.pastOrderRow?.orderHeader,
        orderItems: availableItems,
      },
      productMap,
    );
    await this.modalController.dismiss();
  }

  async closeModal() {
    this.orderTracingService.traceCancelCopyOrderModal(
      this.pastOrderRow?.orderHeader,
    );
    await this.modalController.dismiss();
  }
}
